/* Drives the tablet/desktop product gallery:
   snippets/tuti-desktop-gallery.liquid.

   Same lineup and the same two-way shade binding as the phone gallery
   (assets/tuti-mobile-gallery.js) — the big image's scrollLeft is the
   single source of truth, and everything else is derived from it.

   What differs is the strip. On phones it is a scroller; here it is a
   set of fixed slots that the layout's CSS positions, and this decides
   which photo goes in which slot. A scroller cannot turn the corner in
   the 1280-1679 L layout, and a sliding window of slots gives all four
   layouts one mechanism rather than three plus a special case.

   Two things come from the CSS rather than being repeated here, so the
   layouts stay defined in one place:
     --tuti-dgal-slots    how many slots this layout shows
     --tuti-dgal-reverse  1 when the strip reads backwards (the L: new
                          photos arrive bottom right and travel up the
                          left side, so the highest index sits in slot 0) */
(function () {
  var root = document.querySelector('[data-tuti-dgal]');
  if (!root) return;

  var track = root.querySelector('[data-dgal-track]');
  var strip = root.querySelector('[data-dgal-strip]');
  if (!track) return;

  var slides = Array.prototype.slice.call(track.querySelectorAll('.tuti-dgal__slide'));
  var thumbs = strip ? Array.prototype.slice.call(strip.querySelectorAll('.tuti-dgal__thumb')) : [];
  var prevButton = root.querySelector('[data-dgal-prev]');
  var nextButton = root.querySelector('[data-dgal-next]');
  var picker = document.querySelector('variant-picker');

  if (slides.length < 2) return;

  var frame = null;
  var variantTimer = null;
  var lastIndex = -1;
  var lastSlots = -1;

  function isActive() {
    return root.offsetParent !== null;
  }

  /* Exact rather than approximate: the CSS gives every slide
     `flex: 0 0 100%` in a gapless track, so slide N starts at exactly N
     track widths. If the stage ever gains a gutter or a peek, this is
     the line that changes. */
  function currentIndex() {
    var width = track.clientWidth || 1;
    var index = Math.round(track.scrollLeft / width);
    return Math.max(0, Math.min(slides.length - 1, index));
  }

  /* Safari only learned ScrollToOptions in 15.4; before that
     `scrollTo({left: n})` reads as the two-argument form with both
     undefined and scrolls to zero — landing on the wrong photo rather
     than merely skipping the animation. */
  var canAnimateScroll = 'scrollBehavior' in document.documentElement.style;

  function scrollToIndex(index) {
    var target = Math.max(0, Math.min(slides.length - 1, index));
    var left = target * track.clientWidth;
    if (canAnimateScroll) {
      track.scrollTo({ left: left, behavior: 'smooth' });
    } else {
      track.scrollLeft = left;
    }
  }

  /* ---- The strip ---- */

  function readNumber(name, fallback) {
    var raw = getComputedStyle(root).getPropertyValue(name);
    var value = parseFloat(raw);
    return isNaN(value) ? fallback : value;
  }

  function renderStrip(index) {
    if (!thumbs.length) return;

    var declared = Math.max(1, Math.round(readNumber('--tuti-dgal-slots', 8)));
    var reverse = readNumber('--tuti-dgal-reverse', 0) === 1;
    // Compared against the raw declared value, not the clamped one, so
    // the breakpoint check below cannot fire on every observer tick.
    lastSlots = declared;

    /* The photo on the stage is withheld from the strip: it is still a
       position in the lineup, the strip simply skips over it. So the
       window is drawn from the lineup MINUS that one, which is why
       there is an order array rather than a plain index range. */
    var order = [];
    for (var i = 0; i < thumbs.length; i++) {
      if (i !== index) order.push(i);
    }

    /* The widest layout scrolls instead of windowing: there is room for
       the whole lineup down the left, so every photo but the current one
       is placed and the CSS lets the strip overflow and scroll. No
       window, no reading direction — auto-flow takes document order. */
    var scrolls = readNumber('--tuti-dgal-scroll', 0) === 1;

    var slots = scrolls ? order.length : Math.min(declared, order.length);
    var start = 0;

    if (!scrolls) {
      /* Where in the window the photo immediately after the current one
         sits. Reading forwards it sits second, so one earlier photo
         stays on screen for context. Reading backwards — the L — the
         window is laid out from the far end, and slot 2 is the middle of
         the bottom row, which is where the lineup is picked up. */
      var targetOffset = reverse ? slots - 3 : 1;
      targetOffset = Math.max(0, Math.min(slots - 1, targetOffset));

      /* Removing the current photo shifts everything after it down one,
         so the photo that follows it now sits at position `index` in the
         order array — which is the anchor the window hangs off. */
      var anchor = Math.min(index, Math.max(0, order.length - 1));
      start = Math.max(0, Math.min(order.length - slots, anchor - targetOffset));
    }

    var slotOf = {};
    for (var j = 0; j < slots; j++) {
      var photo = order[start + j];
      if (photo === undefined) continue;
      slotOf[photo] = scrolls ? j : reverse ? slots - 1 - j : j;
    }

    for (var k = 0; k < thumbs.length; k++) {
      if (slotOf[k] === undefined) {
        thumbs[k].removeAttribute('data-slot');
      } else {
        thumbs[k].setAttribute('data-slot', slotOf[k]);
      }
    }

    if (scrolls && strip) {
      buildLoop(order, index);

      /* In scroll mode the strip does not reflow around the current
         photo, so it has to be nudged instead — otherwise moving through
         the lineup leaves the column parked wherever it was last
         dragged. Only when the next photo is actually out of view, and
         never fighting a drag that is already showing it.

         In loop mode the nudge is skipped: the run repeats, so there is
         always a copy of the next photo on screen, and jumping to one
         particular copy would yank the column out from under a drag. */
      if (!loopRun) {
        var nextThumb = thumbs[order[Math.min(index, order.length - 1)]];
        if (nextThumb) {
          var top = nextThumb.offsetTop;
          var bottom = top + nextThumb.offsetHeight;
          if (top < strip.scrollTop) {
            strip.scrollTop = top;
          } else if (bottom > strip.scrollTop + strip.clientHeight) {
            strip.scrollTop = bottom - strip.clientHeight;
          }
        }
      }
    }
  }

  /* ---- The loop ----

     The column runs forever in both directions. It is done by repeating
     the run three times and keeping the scroll parked in the middle
     copy: whenever it crosses into the first or last copy it is moved
     back by exactly one run length, which is invisible because the copy
     it lands on is identical to the one it left. So scrolling up from
     the first photo shows the LAST photo above it, which is what fills
     the notch the staggered column opens at the top.

     The clones are inert copies — the originals keep their click
     handlers and the clones are matched back to a photo through
     data-index by a delegated handler on the strip.

     The stagger is applied here, as a class, rather than by data-slot
     parity in CSS. Across a seam the parity of a slot no longer matches
     its physical column whenever the run holds an odd number of photos,
     so the CSS rule would drop the offset onto the wrong side. Counting
     position in the actual flow cannot get that wrong. */

  var loopRun = 0; // height of one copy, 0 when not looping
  var loopWrapping = false;
  var lastLoop = -1; // last value of --tuti-dgal-loop, to spot the switch
  /* Where the column is looking, as a fraction of one run (0 to 1).
     THIS, not scrollTop, is the position of record. Resizing changes the
     square size and so the run's height, and the browser keeps scrollTop
     at the same NUMBER through that — which points somewhere entirely
     different in a run that just got taller or shorter, and reads as the
     column scrolling itself while the window is dragged. Keeping a
     fraction and re-applying it after every resize holds the column on
     the same photos. */
  var loopOffset = 0;

  /* Which way the run repeats. The column layouts scroll the strip
     vertically; the tablet's two rows scroll it sideways, so everything
     below has to read and write the matching axis. */
  function loopIsHorizontal() {
    return readNumber('--tuti-dgal-loop-x', 0) === 1;
  }

  function loopPos() {
    return loopIsHorizontal() ? strip.scrollLeft : strip.scrollTop;
  }

  function setLoopPos(value) {
    if (loopIsHorizontal()) {
      strip.scrollLeft = value;
    } else {
      strip.scrollTop = value;
    }
  }

  /* Re-measure the run from the DOM. Needed on resize, where the squares
     change size but nothing is rebuilt. */
  function measureLoop() {
    if (!strip) return;
    var firstClone = strip.querySelector('[data-dgal-clone]');
    var firstReal = strip.querySelector(
      '.tuti-dgal__thumb[data-slot]:not([data-dgal-clone])'
    );
    if (!firstClone || !firstReal) {
      loopRun = 0;
      return;
    }
    loopRun = loopIsHorizontal()
      ? firstClone.offsetLeft - firstReal.offsetLeft
      : firstClone.offsetTop - firstReal.offsetTop;
  }

  /* Setting scrollTop fires its scroll event on a LATER frame, so the
     guard has to outlive this function. Clearing it synchronously left
     the flag down by the time the event arrived, and the handler then
     recomputed the offset from a scroll position that was mid-correction
     — which is how a resize could lock in a wrong offset and leave the
     column parked somewhere else in the lineup. */
  function guardScroll() {
    loopWrapping = true;
    requestAnimationFrame(function () {
      loopWrapping = false;
    });
  }

  /* Put the run back where it was looking, in the middle copy. */
  function anchorLoop() {
    if (!loopRun || !strip) return;
    guardScroll();
    setLoopPos(loopRun + loopOffset * loopRun);
  }

  function buildLoop(order, current) {
    if (!strip) return;

    var loops = readNumber('--tuti-dgal-loop', 0) === 1;

    /* Where the column was before this rebuild, as a fraction of one
       run. Rebuilds happen on every photo change AND on every resize, so
       without this the column jumped back to the top of the middle copy
       each time — which read as the strip scrolling itself while the
       window was being dragged. */
    var previousRun = loopRun;
    var previousTop = loopPos();

    // Clear any previous clones before measuring or rebuilding.
    var old = strip.querySelectorAll('[data-dgal-clone]');
    for (var c = 0; c < old.length; c++) old[c].remove();
    loopRun = 0;

    lastLoop = loops ? 1 : 0;

    if (!loops) {
      for (var s = 0; s < thumbs.length; s++) thumbs[s].classList.remove('is-staggered');
      return;
    }

    /* Position in the flow decides the stagger: with two columns the
       odd positions are the right-hand one. */
    var sequence = [];
    for (var i = 0; i < order.length; i++) {
      if (order[i] !== current) sequence.push(thumbs[order[i]]);
    }
    for (var j = 0; j < sequence.length; j++) {
      sequence[j].classList.toggle('is-staggered', j % 2 === 1);
    }

    if (!sequence.length) return;

    /* Two more copies, so there is a full run above and below the one
       being looked at. */
    for (var copy = 0; copy < 2; copy++) {
      for (var k = 0; k < sequence.length; k++) {
        var clone = sequence[k].cloneNode(true);
        clone.setAttribute('data-dgal-clone', '');
        clone.setAttribute('tabindex', '-1');
        clone.setAttribute('aria-hidden', 'true');
        strip.appendChild(clone);
      }
    }

    /* One run's length, from the first item of the run to the first item
       of its first copy. Through measureLoop so it reads the correct
       axis — measuring offsetTop on the tablet's sideways strip gave 0,
       which switched the loop off entirely. */
    measureLoop();

    /* Keep looking at the same place in the lineup across the rebuild.
       previousRun is 0 on the very first build, which parks it at the
       top of the middle copy. */
    if (previousRun > 0) {
      loopOffset = ((previousTop - previousRun) / previousRun) % 1;
      if (loopOffset < 0) loopOffset += 1;
    }
    anchorLoop();
  }

  /* Keep the scroll inside the middle copy, and remember where it is as
     a fraction of the run so a resize can put it back. */
  function onStripScroll() {
    if (!loopRun || loopWrapping) return;
    var at = loopPos();
    if (at < loopRun * 0.5) {
      guardScroll();
      at += loopRun;
      setLoopPos(at);
    } else if (at > loopRun * 1.5) {
      guardScroll();
      at -= loopRun;
      setLoopPos(at);
    }
    loopOffset = (at - loopRun) / loopRun;
  }

  /* ---- Shade picker ---- */

  function selectVariant(variantId) {
    if (!picker || !variantId) return;
    // Already current: either nothing changed, or this is the echo of a
    // change we made ourselves.
    if (root.dataset.selectedVariant === variantId) return;

    var input = picker.querySelector('input[type="radio"][data-variant-id="' + variantId + '"]');
    if (!input) return;

    // Written before the dispatch, so the event this triggers comes
    // back to a root that already agrees and stops there.
    root.dataset.selectedVariant = variantId;

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ---- The one state change everything hangs off ---- */

  function syncTo(index, force) {
    if (index === lastIndex && !force) return;
    lastIndex = index;

    renderStrip(index);

    if (prevButton) prevButton.disabled = index <= 0;
    if (nextButton) nextButton.disabled = index >= slides.length - 1;

    /* Debounced: dragging across a shade boundary should not fire a
       section request per photo. The picker aborts its own in-flight
       fetch, so this is belt and braces — but it also stops the URL's
       ?variant being rewritten on every frame of a momentum scroll. */
    /* The shade CIRCLE follows the gallery from right here: this is the
       one place that knows which slide is current, so it is the one
       place that should say so. (Landing on a slide used to also check
       that shade's radio — that stays off: paging the photos must not
       change the shopper's selection. selectVariant is kept below for
       the picker-driven path's bookkeeping.) */
    /* Debounced on the same timer the old selectVariant path used, and
       for the same reason: this runs on every frame of a scroll, and at
       a shade boundary the index flip-flops between the two sides, which
       made the circle jitter. Only the slide still current 180ms after
       the last movement gets painted. */
    if (variantTimer) clearTimeout(variantTimer);
    variantTimer = setTimeout(function () {
      var bubbleSlide = slides[index];
      if (bubbleSlide && window.tutiShadeBubble) {
        window.tutiShadeBubble(bubbleSlide.dataset.variantId);
      }
    }, 180);
    return;
  }

  function onTrackScroll() {
    if (frame) return;
    frame = requestAnimationFrame(function () {
      frame = null;
      syncTo(currentIndex());
    });
  }

  /* ---- Wiring ---- */

  track.addEventListener('scroll', onTrackScroll, { passive: true });

  /* Delegated, so the loop's clones work without being wired
     individually — they carry the same data-index as the thumbnail they
     were copied from. */
  if (strip) {
    strip.addEventListener('click', function (event) {
      var thumb = event.target.closest && event.target.closest('.tuti-dgal__thumb');
      if (!thumb || !strip.contains(thumb)) return;
      var index = parseInt(thumb.getAttribute('data-index'), 10);
      if (isNaN(index)) return;
      scrollToIndex(index);
      // Don't wait for the scroll to report back: the strip should move
      // under the pointer that clicked it, not a frame later.
      syncTo(index);
    });

    strip.addEventListener('scroll', onStripScroll, { passive: true });
  }

  if (prevButton) {
    prevButton.addEventListener('click', function () {
      scrollToIndex(currentIndex() - 1);
    });
  }

  if (nextButton) {
    nextButton.addEventListener('click', function () {
      scrollToIndex(currentIndex() + 1);
    });
  }

  /* Picker -> gallery. Choosing a swatch jumps to that shade's first
     photo. Guarded the same way as the other direction: if the root
     already names this variant the event is our own echo. */
  if (picker) {
    picker.addEventListener('shopify:product:select', function (event) {
      var variantId = event.detail && event.detail.variantId;
      if (!variantId || root.dataset.selectedVariant === String(variantId)) return;

      root.dataset.selectedVariant = String(variantId);

      for (var i = 0; i < slides.length; i++) {
        if (slides[i].dataset.variantId === String(variantId)) {
          scrollToIndex(i);
          syncTo(i);
          return;
        }
      }
    });
  }

  /* The slot count and reading direction are per layout, so crossing a
     breakpoint has to re-place the window even though the current photo
     has not changed — otherwise the strip keeps the previous layout's
     count and the new layout's last slot sits empty.

     A ResizeObserver on the gallery itself rather than a window resize
     listener: it is driven by the element actually being laid out at a
     new size, which is the thing that matters here, and it fires in
     cases a resize event does not (a zoom, a scrollbar appearing, or a
     container query changing the column width without the window
     moving). The resize listener stays as a fallback for browsers
     without the observer. */
  function relayoutIfNeeded() {
    var slots = Math.round(readNumber('--tuti-dgal-slots', 8));
    var loops = Math.round(readNumber('--tuti-dgal-loop', 0));

    /* Crossing between a looping layout and a non-looping one has to
       rebuild, not just re-anchor. The clones only exist while the loop
       is on, and they are added and removed inside renderStrip — so
       resizing out of the looping band used to leave every clone sitting
       in the DOM. The layout that took over knows nothing about them and
       laid all of them out at full size, which is the wall of photos
       that appeared under the gallery. */
    if (slots !== lastSlots || loops !== lastLoop) {
      lastLoop = loops;
      syncTo(currentIndex(), true);
      return;
    }

    /* No rebuild needed, but the squares may have just changed size, and
       with them the height of a run. scrollTop is still the old number,
       which now points at a different part of the lineup — that is the
       column appearing to scroll itself as the window is dragged. Take a
       fresh measurement and put it back where it was looking. */
    measureLoop();
    anchorLoop();
  }

  if (typeof ResizeObserver === 'function') {
    var observer = new ResizeObserver(relayoutIfNeeded);
    observer.observe(root);
    /* The strip too: a container query can change the column count (and
       so the run's height) without the gallery's own box moving at all,
       which the root observer would never see. */
    if (strip) observer.observe(strip);
  } else {
    window.addEventListener('resize', relayoutIfNeeded, { passive: true });
  }

  /* Initial state. The inline script in the snippet has already put the
     track on the right slide, so this reads it rather than setting it. */
  syncTo(currentIndex(), true);
})();
